import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

export default function Retreats() {
  const [items, setItems] = useState([]);
  const { t } = useContent("retreats");
  useEffect(() => { api.get("/retreats").then(r => setItems(r.data)).catch(() => {}); }, []);
  const upcoming = items.filter(i => i.status === "upcoming");
  const past = items.filter(i => i.status === "past");

  return (
    <div>
      <PageSEO
        title={"Retreats | Celestial Seed Sanctuary"}
        description={"Small-group retreats in the cloud forests of Cundinamarca, Colombia. Application-only. Long integration. Breathwork, ecology, arts, and community practice."}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-8">
        <div className="overline mb-6" data-testid="retreats-overline">{t("hero.overline", "Retreats")}</div>
        <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="retreats-title">
          {t("hero.title", "Gathering points along the path.")}
        </h1>
        <p className="text-ink-soft text-xl mt-8 max-w-2xl" data-testid="retreats-lead">
          {t("hero.lead", "Each retreat is a threshold. Application only. Small groups. Long integration.")}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <Tabs defaultValue="upcoming">
          <TabsList className="bg-surface border border-line">
            <TabsTrigger value="upcoming" data-testid="retreats-tab-upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past" data-testid="retreats-tab-past">Past</TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming" className="mt-8">
            <RetreatGrid items={upcoming} />
          </TabsContent>
          <TabsContent value="past" className="mt-8">
            <RetreatGrid items={past} pastMode />
          </TabsContent>
        </Tabs>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <div className="overline mb-3" data-testid="retreats-practicalities-overline">{t("practicalities.overline", "Practicalities")}</div>
        <h2 className="font-serif text-4xl mb-8" data-testid="retreats-practicalities-headline">{t("practicalities.headline", "What to know before applying")}</h2>
        <div className="grid sm:grid-cols-2 gap-6 text-ink-soft">
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Deposits</div><p className="mt-1 text-sm">A non-refundable deposit secures your place. Balance is due 30 days before arrival.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Transportation</div><p className="mt-1 text-sm">Fly into Bogotá (BOG) — El Dorado International. We arrange collective ground transport from Bogotá to the sanctuary in Cundinamarca (approximately 2 hours).</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Climate</div><p className="mt-1 text-sm">Cloud-forest climate. Cool mornings (10–15°C / 50–59°F), mild afternoons (18–22°C / 64–72°F), and frequent light rain year-round.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Elevation</div><p className="mt-1 text-sm">Approximately 1,800 meters (6,000 feet) above sea level. Allow one day to acclimate on arrival.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Clothing recommendations</div><p className="mt-1 text-sm">Layers you can add and remove. A warm sweater or fleece for evenings. Long sleeves, long pants, and comfortable clothing for ceremony and practice.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">What to bring</div><p className="mt-1 text-sm">Sturdy closed shoes, rain jacket, water bottle, headlamp or small flashlight, journal, personal medications, and an open heart.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Land conditions</div><p className="mt-1 text-sm">Uneven natural paths, streams, and forest terrain. Some paths become muddy after rain. Mobility considerations should be discussed during intake.</p></div>
          <div className="border-t border-line pt-4"><div className="font-serif text-xl text-ink">Medical</div><p className="mt-1 text-sm">All applicants complete a private intake. We hold trauma-informed space.</p></div>
        </div>
      </section>
    </div>
  );
}

function RetreatGrid({ items, pastMode = false }) {
  if (!items.length) return <div className="text-ink-soft py-16 text-center">Nothing here yet. Please check back.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-8">
      {items.map((r) => (
        <div key={r.id} className="rounded-3xl overflow-hidden border border-line bg-surface" data-testid={`retreat-item-${r.id}`}>
          <div className="aspect-[16/10] overflow-hidden"><img src={r.image_url} alt={r.title} className="w-full h-full object-cover" /></div>
          <div className="p-8">
            <div className="overline mb-2">{dayjs(r.start_date).format("MMM D")} — {dayjs(r.end_date).format("MMM D, YYYY")}</div>
            <div className="font-serif text-3xl leading-tight">{r.title}</div>
            <p className="text-ink-soft mt-3 leading-relaxed">{r.description}</p>
            <div className="mt-6 flex items-center justify-between gap-3">
              <div className="text-ink"><span className="font-serif text-2xl">${r.price}</span><span className="text-ink-soft text-sm"> / person</span></div>
              <div className="flex items-center gap-2">
                <Link to={`/retreats/${r.id}`} data-testid={`retreat-details-${r.id}`} className="pill pill-outline text-sm">View Details</Link>
                {!pastMode && <ApplyDialog retreat={r} />}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApplyDialog({ retreat }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", intentions: "", dietary: "", medical: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/retreats/apply", { ...form, retreat_id: retreat.id, retreat_title: retreat.title });
      toast.success("Application received. We'll be in touch within 3 days.");
      setOpen(false); setForm({ name: "", email: "", phone: "", intentions: "", dietary: "", medical: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="pill pill-primary text-sm" data-testid={`apply-${retreat.id}`}>Apply</button>
      </DialogTrigger>
      <DialogContent className="bg-bone max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Apply — {retreat.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Full name"><Input required data-testid="apply-name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} /></Field>
          <Field label="Email"><Input type="email" required data-testid="apply-email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} /></Field>
          <Field label="Phone"><Input data-testid="apply-phone" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} /></Field>
          <Field label="Your intentions"><Textarea rows={3} data-testid="apply-intentions" value={form.intentions} onChange={e=>setForm({...form, intentions: e.target.value})} /></Field>
          <Field label="Dietary needs"><Input data-testid="apply-dietary" value={form.dietary} onChange={e=>setForm({...form, dietary: e.target.value})} /></Field>
          <Field label="Medical notes"><Input data-testid="apply-medical" value={form.medical} onChange={e=>setForm({...form, medical: e.target.value})} /></Field>
          <Button type="submit" disabled={busy} data-testid="apply-submit" className="w-full bg-forest text-bone hover:bg-ink rounded-full py-6">{busy ? "Sending..." : "Send Application"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><Label className="overline mb-2 block">{label}</Label>{children}</div>; }
